import React, { useState, useMemo, useCallback } from 'react'
import { useTheme } from '@dhl-ui/theme'
import { Snackbar } from './Snackbar'
import type { SnackbarOptions, SnackbarKey, SnackbarMessage, SnackbarProviderProps } from './types'
import { normalizeToastOptions } from './normalize-toast-options'
import { SnackbarContext, type SnackbarContent } from './SnackBarContext'

export const SnackbarProvider = ({ children, position, autoHideDuration, width }: SnackbarProviderProps) => {
  const [toastsPack, setToastsPack] = useState<SnackbarContent>({})
  const { variant } = useTheme()

  const addToast = useCallback(
    (message: SnackbarMessage, toastOptions?: SnackbarOptions) => {
      const toastNormalized = normalizeToastOptions(
        message,
        {
          position,
          autoHideDuration,
          width,
        },
        variant,
        toastOptions
      )

      setToastsPack((currentToasts) => {
        const positionKey = toastNormalized.position
        const toasts = currentToasts[positionKey] || []

        const duplicateToastMessage = toasts.find((toast) => toast.message === message)
        if (toastNormalized.preventDuplicate && duplicateToastMessage?.key) {
          return currentToasts
        }

        let rest = [...toasts]
        if (toasts.length >= 3) {
          rest = rest.map((elm, idx) => ({
            ...elm,
            visible: idx < 2,
          }))
        }

        return {
          ...currentToasts,
          [positionKey]: [toastNormalized, ...rest],
        }
      })

      return toastNormalized.key
    },
    [position, autoHideDuration, width, variant]
  )

  const removeToast = useCallback((toastKey: SnackbarKey) => {
    setToastsPack((prev) =>
      Object.entries(prev).reduce(
        (cur, [key, value]) => ({
          ...cur,
          [key]: value.filter((toast) => toast.key !== toastKey),
        }),
        {}
      )
    )
  }, [])

  const dismissAll = useCallback(() => {
    setToastsPack({})
  }, [])

  const providerValues = useMemo(() => ({ addToast, removeToast, dismissAll }), [addToast, removeToast, dismissAll])

  return (
    <SnackbarContext.Provider value={providerValues}>
      {children}
      {Object.entries(toastsPack).map(([key, toasts]) => (
        <Snackbar key={key} toasts={toasts} />
      ))}
    </SnackbarContext.Provider>
  )
}
